"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  FaArrowLeft,
  FaChevronRight,
  FaClipboardList,
  FaComments,
  FaEdit,
  FaExternalLinkAlt,
  FaSearch,
  FaUser,
} from "react-icons/fa";
import Header from "../../../components/ManagementHeader";
import Footer from "../../../components/Footer";
import { supabase } from "../../../supabaseClient";

const APPLICATION_STATUSES = ["Received", "Under Review", "Accepted", "Denied"];

function formatDate(value, includeTime = false) {
  if (!value) return "Not set";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Not set";
  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    ...(includeTime ? { hour: "2-digit", minute: "2-digit" } : {}),
  });
}

function normaliseQuestions(value) {
  if (!Array.isArray(value)) return [];
  return value.map((question, index) => {
    if (typeof question === "string") {
      return { id: `legacy-${index}`, label: question, type: "long", required: true };
    }
    return {
      id: question?.id || `question-${index}`,
      label: question?.label || question?.question || `Question ${index + 1}`,
      type: question?.type || "long",
      required: question?.required !== false,
      options: Array.isArray(question?.options) ? question.options : [],
    };
  });
}

function StatusBadge({ status }) {
  const key = String(status || "Received").toLowerCase().replace(/\s+/g, "-");
  return <span className={`pguh-recruitment-status pguh-recruitment-status--${key}`}>{status}</span>;
}

export default function VacancyApplications() {
  const { id } = useParams();
  const [vacancy, setVacancy] = useState(null);
  const [applications, setApplications] = useState([]);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [chat, setChat] = useState([]);
  const [chatMessage, setChatMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [savingDecision, setSavingDecision] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const chatChannelRef = useRef(null);

  useEffect(() => {
    void loadPage();
    return () => cleanupChatSubscription();
  }, [id]);

  async function loadPage() {
    setLoading(true);
    setError("");

    const [vacancyResult, applicationResult] = await Promise.all([
      supabase.from("dc_vacancies").select("*").eq("id", id).single(),
      supabase.from("dc_applications").select("*").eq("vacancy_id", id).order("created_at", { ascending: false }),
    ]);

    if (vacancyResult.error) {
      setError(vacancyResult.error.message || "The vacancy could not be loaded.");
      setVacancy(null);
    } else {
      setVacancy(vacancyResult.data || null);
    }

    if (applicationResult.error) {
      setError((current) => current || applicationResult.error.message || "Applications could not be loaded.");
      setApplications([]);
    } else {
      setApplications(applicationResult.data || []);
    }

    setLoading(false);
  }

  function cleanupChatSubscription() {
    if (chatChannelRef.current) {
      supabase.removeChannel(chatChannelRef.current);
      chatChannelRef.current = null;
    }
  }

  function subscribeToChat(applicationId) {
    cleanupChatSubscription();
    chatChannelRef.current = supabase
      .channel(`dc_application_chat_management_${applicationId}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "dc_application_chat",
          filter: `application_id=eq.${applicationId}`,
        },
        (payload) => setChat((current) => [...current, payload.new]),
      )
      .subscribe();
  }

  async function openApplication(application) {
    setSelectedApplication(application);
    setChat([]);
    setChatMessage("");
    cleanupChatSubscription();

    const { data, error: chatError } = await supabase
      .from("dc_application_chat")
      .select("*")
      .eq("application_id", application.id)
      .order("created_at", { ascending: true });

    if (chatError) {
      setError(chatError.message || "Application chat could not be loaded.");
    } else {
      setChat(data || []);
    }

    subscribeToChat(application.id);
  }

  async function sendChatMessage() {
    const message = chatMessage.trim();
    if (!message || !selectedApplication) return;

    const { error: sendError } = await supabase.rpc("ruht_send_application_chat_message", {
      p_application_id: selectedApplication.id,
      p_message: message,
    });

    if (sendError) {
      setError(sendError.message || "The message could not be sent.");
      return;
    }

    setChatMessage("");
  }

  async function setDecision(nextStatus) {
    if (!selectedApplication || savingDecision) return;
    setSavingDecision(true);
    setError("");

    const { data, error: updateError } = await supabase
      .from("dc_applications")
      .update({ status: nextStatus })
      .eq("id", selectedApplication.id)
      .select("*")
      .single();

    if (updateError) {
      setError(updateError.message || "The application status could not be updated.");
    } else if (data) {
      setSelectedApplication(data);
      setApplications((current) => current.map((item) => (item.id === data.id ? data : item)));
    }

    setSavingDecision(false);
  }

  const counts = useMemo(() => ({
    total: applications.length,
    received: applications.filter((item) => item.status === "Received").length,
    review: applications.filter((item) => item.status === "Under Review").length,
    accepted: applications.filter((item) => item.status === "Accepted").length,
    denied: applications.filter((item) => item.status === "Denied").length,
  }), [applications]);

  const filteredApplications = useMemo(() => {
    const query = search.trim().toLowerCase();
    return applications.filter((application) => {
      const matchesStatus = statusFilter === "All" || application.status === statusFilter;
      const matchesSearch = !query || [
        application.roleplay_name,
        application.roblox_username,
        application.discord_username,
        application.email,
      ].filter(Boolean).some((value) => String(value).toLowerCase().includes(query));
      return matchesStatus && matchesSearch;
    });
  }, [applications, search, statusFilter]);

  const customQuestions = normaliseQuestions(vacancy?.questions);
  const submittedAnswers = selectedApplication?.submitted_answers || {};
  const vacancyOpen = vacancy?.status === "Open" && vacancy?.is_active;

  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      <main className="flex-1 pguh-vacancy-applications-page">
        <div className="nhsuk-width-container">
          <Link className="pguh-vacancy-applications-back" to="/intranet/management/vacancies">
            <FaArrowLeft aria-hidden="true" /> Back to vacancies
          </Link>

          {error && (
            <div className="nhsuk-error-summary" role="alert">
              <h2 className="nhsuk-error-summary__title">There is a problem</h2>
              <div className="nhsuk-error-summary__body">{error}</div>
            </div>
          )}

          {loading ? (
            <div className="pguh-vacancy-applications-loading">Loading vacancy applications…</div>
          ) : !vacancy ? (
            <section className="pguh-vacancy-applications-empty">
              <FaClipboardList aria-hidden="true" />
              <h1>Vacancy not found</h1>
              <p>This vacancy may have been removed or you may not have access to it.</p>
            </section>
          ) : (
            <>
              <section className="pguh-vacancy-applications-hero">
                <div className="pguh-vacancy-applications-hero__main">
                  <div className="pguh-vacancy-applications-hero__status">
                    <StatusBadge status={vacancyOpen ? "Open" : vacancy.status || "Closed"} />
                    <span>Vacancy ID: {vacancy.id}</span>
                  </div>
                  <span className="pguh-eyebrow">Management recruitment</span>
                  <h1>{vacancy.title}</h1>
                  <p>{vacancy.description || "No vacancy summary has been added yet."}</p>
                  <div className="pguh-vacancy-applications-actions">
                    <Link className="nhsuk-button" to={`/intranet/management/vacancies/${vacancy.id}`}>
                      <FaEdit aria-hidden="true" /> Edit vacancy
                    </Link>
                    <Link className="nhsuk-button nhsuk-button--secondary" to={`/vacancies/${vacancy.id}`}>
                      <FaExternalLinkAlt aria-hidden="true" /> Public preview
                    </Link>
                  </div>
                </div>
                <dl className="pguh-vacancy-applications-summary">
                  <div><dt>Department</dt><dd>{vacancy.department || "Not specified"}</dd></div>
                  <div><dt>Location</dt><dd>{vacancy.location || "Not specified"}</dd></div>
                  <div><dt>Closing date</dt><dd>{formatDate(vacancy.closing_date, true)}</dd></div>
                  <div><dt>Questions</dt><dd>4 fixed + {customQuestions.length} custom</dd></div>
                </dl>
              </section>

              <section className="pguh-vacancy-applications-stats" aria-label="Application summary">
                <div><span>Total applications</span><strong>{counts.total}</strong><small>All submissions for this vacancy</small></div>
                <div><span>Received</span><strong>{counts.received}</strong><small>Awaiting initial review</small></div>
                <div><span>Under review</span><strong>{counts.review}</strong><small>Currently being assessed</small></div>
                <div><span>Accepted</span><strong>{counts.accepted}</strong><small>Successful applications</small></div>
                <div><span>Denied</span><strong>{counts.denied}</strong><small>Unsuccessful applications</small></div>
              </section>

              <section className="pguh-vacancy-applications-workspace">
                <aside className="pguh-vacancy-applications-sidebar">
                  <div className="pguh-vacancy-applications-sidebar__header">
                    <div><span className="pguh-eyebrow">Applications</span><h2>Applicants</h2></div>
                    <strong>{filteredApplications.length}</strong>
                  </div>
                  <div className="pguh-vacancy-applications-filters">
                    <label className="pguh-vacancy-applications-search">
                      <FaSearch aria-hidden="true" />
                      <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search applicants" />
                    </label>
                    <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)} aria-label="Filter application status">
                      <option>All</option>
                      {APPLICATION_STATUSES.map((status) => <option key={status}>{status}</option>)}
                    </select>
                  </div>

                  <div className="pguh-vacancy-applications-list">
                    {filteredApplications.length === 0 ? (
                      <div className="pguh-vacancy-applications-list__empty">
                        <FaUser aria-hidden="true" />
                        <p>No applications match this view.</p>
                      </div>
                    ) : filteredApplications.map((application) => (
                      <button
                        type="button"
                        key={application.id}
                        className={selectedApplication?.id === application.id ? "is-selected" : ""}
                        onClick={() => openApplication(application)}
                      >
                        <span className="pguh-vacancy-applications-list__identity">
                          <strong>{application.roleplay_name || application.discord_username || "Applicant"}</strong>
                          <small>{application.roblox_username || application.discord_username || application.email || "No identifier"}</small>
                          <time>{formatDate(application.created_at, true)}</time>
                        </span>
                        <span className="pguh-vacancy-applications-list__status">
                          <StatusBadge status={application.status || "Received"} />
                          <FaChevronRight aria-hidden="true" />
                        </span>
                      </button>
                    ))}
                  </div>
                </aside>

                <div className="pguh-vacancy-application-review">
                  {!selectedApplication ? (
                    <div className="pguh-vacancy-application-review__empty">
                      <FaClipboardList aria-hidden="true" />
                      <h2>Select an application</h2>
                      <p>Choose an applicant to review their submitted details, answers, status and messages.</p>
                    </div>
                  ) : (
                    <>
                      <header className="pguh-vacancy-application-review__header">
                        <div>
                          <span className="pguh-eyebrow">Applicant</span>
                          <h2>{selectedApplication.roleplay_name || selectedApplication.discord_username || "Applicant"}</h2>
                          <p>Submitted {formatDate(selectedApplication.created_at, true)}</p>
                        </div>
                        <label>
                          <span>Application status</span>
                          <select value={selectedApplication.status || "Received"} disabled={savingDecision} onChange={(event) => setDecision(event.target.value)}>
                            {APPLICATION_STATUSES.map((status) => <option key={status}>{status}</option>)}
                          </select>
                        </label>
                      </header>

                      <section className="pguh-vacancy-application-section">
                        <h3>Applicant details</h3>
                        <dl className="pguh-vacancy-application-details">
                          <div><dt>Roblox username</dt><dd>{selectedApplication.roblox_username || "Not provided"}</dd></div>
                          <div><dt>Discord username</dt><dd>{selectedApplication.discord_username || "Not provided"}</dd></div>
                          <div><dt>Email</dt><dd>{selectedApplication.email || "Not provided"}</dd></div>
                          <div><dt>Roleplay name</dt><dd>{selectedApplication.roleplay_name || "Not provided"}</dd></div>
                        </dl>
                      </section>

                      <section className="pguh-vacancy-application-section">
                        <h3>Application answers</h3>
                        {customQuestions.length === 0 ? (
                          <p className="pguh-vacancy-application-muted">This vacancy has no custom questions.</p>
                        ) : (
                          <div className="pguh-vacancy-application-answers">
                            {customQuestions.map((question, index) => {
                              const answer = submittedAnswers[question.id];
                              const displayAnswer = Array.isArray(answer) ? answer.join(", ") : answer || "No answer provided";
                              return (
                                <article key={question.id}>
                                  <span>Question {index + 1}</span>
                                  <h4>{question.label}</h4>
                                  <p>{displayAnswer}</p>
                                </article>
                              );
                            })}
                          </div>
                        )}
                      </section>

                      <section className="pguh-vacancy-application-section pguh-vacancy-application-chat">
                        <div className="pguh-vacancy-application-chat__heading">
                          <div><FaComments aria-hidden="true" /><h3>Application chat</h3></div>
                          <span>{chat.length} message{chat.length === 1 ? "" : "s"}</span>
                        </div>
                        <div className="pguh-vacancy-application-chat__log" aria-live="polite">
                          {chat.length === 0 ? <p>No messages yet.</p> : chat.map((message) => (
                            <div key={message.id} className={`pguh-vacancy-application-chat__message is-${message.sender}`}>
                              <strong>{message.sender === "staff" ? "Management" : "Applicant"}</strong>
                              <p>{message.message}</p>
                              <time>{formatDate(message.created_at, true)}</time>
                            </div>
                          ))}
                        </div>
                        <div className="pguh-vacancy-application-chat__compose">
                          <textarea rows="3" value={chatMessage} onChange={(event) => setChatMessage(event.target.value)} placeholder="Write a message to the applicant…" maxLength={1500} />
                          <button type="button" className="nhsuk-button" onClick={sendChatMessage} disabled={!chatMessage.trim()}>Send message</button>
                        </div>
                      </section>
                    </>
                  )}
                </div>
              </section>
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
